/**
 * Centralized structured logging with environment-aware levels and pluggable transports.
 *
 * Log levels by environment tier:
 *   development -> DEBUG  (full visibility)
 *   uat         -> INFO   (QA-level, no debug noise)
 *   production  -> WARN   (errors and warnings only)
 *
 * Transports are configured via config.logTransports (comma-separated).
 * Built-in: console, datadog, newrelic.
 */

import { config } from '@/config';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

interface LogEntry {
  timestamp: string;
  level: string;
  service: string;
  module: string;
  message: string;
  environment: string;
  trace_id?: string;
  data?: unknown;
  error?: { type: string; message: string };
}

export interface LogTransport {
  emit(entry: LogEntry): void;
}

// ---------------------------------------------------------------------------
// Sensitive-field redaction
// ---------------------------------------------------------------------------

const REDACTED_VALUE = '[REDACTED]';
const SENSITIVE_KEY_PATTERN = /(?:authorization|token|api[-_]?key|secret|password|credential)/i;
const MAX_RECURSION_DEPTH = 4;

/**
 * Recursively redact sensitive fields and cap object depth for safe log emission.
 *
 * @param value - Any value to sanitise.
 * @param depth - Current recursion depth; truncates at MAX_RECURSION_DEPTH.
 * @returns A sanitised copy safe to include in a log entry.
 */
const sanitize = (value: unknown, depth = 0): unknown => {
  if (value === null || value === undefined) {
    return value;
  }
  if (depth > MAX_RECURSION_DEPTH) {
    return '[Truncated]';
  }

  if (value instanceof Error) {
    return { type: value.name, message: value.message };
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitize(item, depth + 1));
  }

  if (typeof value === 'object') {
    const source = value as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    for (const [key, nested] of Object.entries(source)) {
      result[key] = SENSITIVE_KEY_PATTERN.test(key) ? REDACTED_VALUE : sanitize(nested, depth + 1);
    }
    return result;
  }

  return value;
};

// ---------------------------------------------------------------------------
// Environment-tier level resolution
// ---------------------------------------------------------------------------

const ENV_LEVEL_DEFAULTS: Record<string, LogLevel> = {
  development: LogLevel.DEBUG,
  dev: LogLevel.DEBUG,
  uat: LogLevel.INFO,
  staging: LogLevel.INFO,
  production: LogLevel.WARN,
  prod: LogLevel.WARN,
};

const LEVEL_NAME_MAP: Record<string, LogLevel> = {
  DEBUG: LogLevel.DEBUG,
  INFO: LogLevel.INFO,
  WARN: LogLevel.WARN,
  ERROR: LogLevel.ERROR,
};

/** Resolve the active log level from VITE_LOG_LEVEL override, then environment tier defaults. */
const resolveLevel = (): LogLevel => {
  const explicit = config.logLevel;
  if (explicit && LEVEL_NAME_MAP[explicit.toUpperCase()] !== undefined) {
    return LEVEL_NAME_MAP[explicit.toUpperCase()];
  }
  return ENV_LEVEL_DEFAULTS[config.environment.toLowerCase()] ?? LogLevel.INFO;
};

// ---------------------------------------------------------------------------
// Built-in transports
// ---------------------------------------------------------------------------

/* eslint-disable no-console -- ConsoleTransport is the intentional browser devtools sink */
class ConsoleTransport implements LogTransport {
  emit(entry: LogEntry): void {
    const line = JSON.stringify(entry);
    switch (entry.level) {
      case 'DEBUG':
        console.debug(line);
        break;
      case 'INFO':
        console.info(line);
        break;
      case 'WARN':
        console.warn(line);
        break;
      case 'ERROR':
        console.error(line);
        break;
      default:
        console.info(line);
    }
  }
}
/* eslint-enable no-console */

class DatadogBrowserTransport implements LogTransport {
  private readonly apiKey: string;
  private readonly site: string;
  private readonly url: string;

  constructor(apiKey: string, site = 'datadoghq.com') {
    this.apiKey = apiKey;
    this.site = site;
    this.url = `https://http-intake.logs.${site}/api/v2/logs`;
  }

  emit(entry: LogEntry): void {
    const payload = {
      ddsource: entry.service,
      ddtags: `env:${entry.environment}`,
      hostname: entry.service,
      message: JSON.stringify(entry),
      service: entry.service,
    };
    try {
      fetch(this.url, {
        method: 'POST',
        headers: { 'DD-API-KEY': this.apiKey, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    } catch {
      // Never let logging break the app
    }
  }
}

class NewRelicBrowserTransport implements LogTransport {
  private readonly licenseKey: string;
  private readonly endpoint: string;

  constructor(licenseKey: string, endpoint = 'https://log-api.newrelic.com/log/v1') {
    this.licenseKey = licenseKey;
    this.endpoint = endpoint;
  }

  emit(entry: LogEntry): void {
    const payload = [{ common: { attributes: { logtype: 'browser' } }, logs: [entry] }];
    try {
      fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Api-Key': this.licenseKey, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    } catch {
      // Never let logging break the app
    }
  }
}

// ---------------------------------------------------------------------------
// Transport registry
// ---------------------------------------------------------------------------

/** Build the transport list from the VITE_LOG_TRANSPORTS config value; defaults to ConsoleTransport. */
const buildTransports = (): LogTransport[] => {
  const names = config.logTransports
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  const result: LogTransport[] = [];

  for (const name of names) {
    if (name === 'console') {
      result.push(new ConsoleTransport());
    } else if (name === 'datadog' && config.datadogApiKey) {
      result.push(new DatadogBrowserTransport(config.datadogApiKey, config.datadogSite));
    } else if (name === 'newrelic' && config.newrelicLicenseKey) {
      result.push(new NewRelicBrowserTransport(config.newrelicLicenseKey, config.newrelicEndpoint));
    }
  }

  if (result.length === 0) {
    result.push(new ConsoleTransport());
  }
  return result;
};

let transports: LogTransport[] | null = null;

/** Lazily initialise and return the singleton transport list. */
const getTransports = (): LogTransport[] => {
  transports ??= buildTransports();
  return transports;
};

// ---------------------------------------------------------------------------
// Logger class
// ---------------------------------------------------------------------------

/**
 * Structured logger bound to a service and module label.
 *
 * Emits sanitised, environment-aware LogEntry objects to all configured transports.
 * Level filtering is evaluated on every emit so it picks up runtime config changes.
 */
export class Logger {
  private readonly service: string;
  private readonly module: string;

  constructor(service: string, module: string) {
    this.service = service;
    this.module = module;
  }

  private get currentLevel(): LogLevel {
    return resolveLevel();
  }

  /** Construct a structured log entry with timestamp, level, and sanitised data. */
  private buildEntry(level: string, message: string, data?: unknown, error?: Error): LogEntry {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      service: this.service,
      module: this.module,
      message,
      environment: config.environment,
    };
    if (data !== undefined) {
      entry.data = sanitize(data);
    }
    if (error) {
      entry.error = { type: error.name, message: error.message };
    }
    return entry;
  }

  /** Check level gate and dispatch to all transports; never throws. */
  private emit(
    level: LogLevel,
    levelName: string,
    message: string,
    data?: unknown,
    error?: Error,
  ): void {
    if (this.currentLevel > level) {
      return;
    }
    try {
      const entry = this.buildEntry(levelName, message, data, error);
      for (const transport of getTransports()) {
        transport.emit(entry);
      }
    } catch {
      // Never let the logging system crash the app
    }
  }

  /** Log at DEBUG level — emitted only in development and when LOG_LEVEL=DEBUG. */
  debug(message: string, data?: unknown): void {
    this.emit(LogLevel.DEBUG, 'DEBUG', message, data);
  }

  /** Log at INFO level — standard operational events. */
  info(message: string, data?: unknown): void {
    this.emit(LogLevel.INFO, 'INFO', message, data);
  }

  /** Log at WARN level — degraded state or unexpected-but-recoverable conditions. */
  warn(message: string, data?: unknown): void {
    this.emit(LogLevel.WARN, 'WARN', message, data);
  }

  /** Log at ERROR level — attach an Error instance for stack/type capture. */
  error(message: string, data?: unknown, error?: Error): void {
    this.emit(LogLevel.ERROR, 'ERROR', message, data, error);
  }
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Create a Logger bound to a specific service and module label.
 *
 * @param service - Application/service identifier (e.g. 'bdp-frontend').
 * @param module - Feature or file label to narrow the source (e.g. 'useAssessmentList').
 * @returns A Logger instance ready for use.
 */
export const createLogger = (service: string, module: string): Logger => {
  return new Logger(service, module);
};
